import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import {
  resetState,
  setLoading,
  setTotal,
  setDocumentTypes,
  setFetch,
} from "../../store/slices/documentTypeSlice.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { paginateDocumentTypes } from "../../services/documentTypes.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import FilterDocumentTypes from "../../components/document-type/FilterDocumentTypes.tsx";
import DocumentTypeTable from "../../components/document-type/DocumentTypeTable.tsx";
import NewDocumentType from "../../components/document-type/NewDocumentType.tsx";
import EditDocumentType from "../../components/document-type/EditDocumentType.tsx";

interface DataToEdit {
  id: number;
  name: string;
  obligatory: boolean;
  maxDayToUpload?: number;
}

const DocumentsTable = () => {
  useAppSettings();
  const dispatch = useDispatch();
  const { page, limit, loading, search, sortBy, defaultSort, fetchTrigger } =
    useSelector((state: RootState) => state.documentType);

  const [showModal, setShowModal] = React.useState(false);
  const [showModalEdit, setShowModalEdit] = React.useState(false);
  const [dataToEdit, setDataToEdit] = React.useState<DataToEdit | null>(null);

  React.useEffect(() => {
    dispatch(resetState());
  }, []);

  React.useEffect(() => {
    fetchDocumentTypes();
  }, [page, limit, sortBy, defaultSort, search, fetchTrigger]);

  const fetchDocumentTypes = async () => {
    dispatch(setLoading(true));
    try {
      const offset = page === 0 ? 0 : page * limit;
      const query = `limit=${limit}&offset=${offset}&sort=${sortBy}&order=${defaultSort}${
        search ? `&search=${encodeURIComponent(search)}` : ""
      }`;
      const response = await paginateDocumentTypes(query);
      dispatch(setDocumentTypes(response.data));
      dispatch(setTotal(response.total));
    } catch (error: any) {
      notification(
        "Error",
        error?.message || "Error al cargar tipos de documento",
        "danger"
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const closeModalAndRefresh = () => {
    setShowModal(false);
    setShowModalEdit(false);
    setDataToEdit(null);
    dispatch(setFetch());
  };

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Tipos de documento"
        basePath="/documents"
        title="Tipos de documento"
      />
      <div className="card border-0 m-4">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <FilterDocumentTypes
              onNewClick={() => {
                setShowModalEdit(false);
                setDataToEdit(null);
                setShowModal(true);
              }}
            />
            <DocumentTypeTable
              onEditClick={(item) => {
                setDataToEdit({
                  id: item.id,
                  name: item.name,
                  obligatory: item.obligatory,
                  maxDayToUpload: item.maxDayToUpload ?? 0,
                });
                setShowModalEdit(true);
                setShowModal(true);
              }}
            />
          </div>
        </div>
      </div>

        <ModalContainer
          open={showModal}
          closeFn={() => {
            setShowModal(false);
            setShowModalEdit(false);
            setDataToEdit(null);
          }}
          title={
            showModalEdit && dataToEdit
              ? "Editar tipo de documento"
              : "Nuevo tipo de documento"
          }
          children={
            showModalEdit && dataToEdit ? (
              <EditDocumentType
                data={dataToEdit}
                closeFn={closeModalAndRefresh}
              />
            ) : (
              <NewDocumentType closeFn={closeModalAndRefresh} />
            )
          }
        />
    </Layout>
  );
};
 
export default DocumentsTable;
